/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
/* The runtime's identity and capabilities, from `state.pieve.runtime`. This file imports no other selector file, so
   `uiSelectors.js` can read a capability without an import cycle; `runtimeSelectors.js` re-exports everything here. */
import { createSelector } from 'reselect';
import { getPieveRuntimeCapabilitiesFromRuntimeMetadata } from '@/lib/platform/pieveRuntimeCapabilities.js';
import { EMPTY_OBJECT } from '@/redux/selectors/pieveSelectors/constants.js';
import createRuntimeCapabilitySelector from '@/redux/selectors/pieveSelectors/lib/createRuntimeCapabilitySelector.js';

export const isDev = () => Boolean(import.meta.env?.DEV);
export const runtime = (state) => state?.pieve?.runtime || EMPTY_OBJECT;
export const runtimeKind = (state) =>
  String(runtime(state)?.kind || '')
    .trim()
    .toLowerCase();
export const runtimePlatform = (state) =>
  String(runtime(state)?.platform || '')
    .trim()
    .toLowerCase();
export const runtimeSurface = (state) => runtime(state)?.surface || EMPTY_OBJECT;
export const runtimeApiBaseUrl = (state) =>
  String(runtime(state)?.apiBaseUrl || '')
    .trim()
    .replace(/\/$/, '');
/* ?singlePage=true on the address that opened this document: every navigation surface stays
   hidden and the page stands alone. */
export const runtimeSinglePage = (state) => runtime(state)?.singlePage === true;
/* The page that visit is pinned to. Addresses outside it are off limits in the frame. */
export const runtimeSinglePageRoot = (state) => (runtimeSinglePage(state) ? String(runtime(state)?.singlePageRoot || '') : '');

export const runtimeCapabilities = createSelector([runtime], (runtimeValue) => {
  return getPieveRuntimeCapabilitiesFromRuntimeMetadata(runtimeValue);
});

export const runtimeIsHydrated = createSelector([runtimeKind], (kind) => kind === 'desktop' || kind === 'web');

export const runtimeIsDesktop = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'isDesktop'
});
export const canPickRepo = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canPickRepo'
});
export const canListRepoBranches = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canListRepoBranches'
});
export const canSwitchRepoBranch = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canSwitchRepoBranch'
});
export const canManageRepoBranches = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canManageRepoBranches'
});
export const canCreateAndSwitchRepoBranch = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canCreateAndSwitchRepoBranch'
});
export const canRunShell = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canRunShell'
});
export const canReadFile = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canReadFile'
});
export const canRunSearch = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canRunSearch'
});
export const canRunAuditBundleReveal = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canRunAuditBundleReveal'
});
export const canCopyRunBundlePath = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canCopyRunBundlePath'
});
export const canUseTerminal = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canUseTerminal'
});
export const canUseAutoUpdates = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canUseAutoUpdates'
});
export const canRespondToApprovals = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canRespondToApprovals'
});
export const canRespondToInteractions = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canRespondToInteractions'
});
export const canReadSharedWorkspaces = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canReadSharedWorkspaces'
});
export const canControlSharedSync = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canControlSharedSync'
});
export const canWindowDrag = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canWindowDrag'
});
// Pieve Desktop with its plan-run bridge: the one runtime that can start, steer, answer and stop a plan run.
export const canRunPlanRuns = createRuntimeCapabilitySelector({
  runtimeCapabilitiesSelector: runtimeCapabilities,
  capabilityKey: 'canRunPlanRuns'
});
